import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import type { UnifiedRecommendation } from '@traaker/shared';
import { RecommendationCard } from './RecommendationCard';
import { auditsApi } from '../../services/audits';

/**
 * The single, standardized recommendations layout used across every audit type.
 * Feed it `UnifiedRecommendation[]` (built via the adapters in
 * @traaker/shared: fromStringList / fromBrandAxis / fromMissingAuthorities /
 * fromEarnedMediaRecos). Each card shows a target icon (task / content / GEO),
 * title, optional description, impact + effort chips, and one action button.
 *
 * Recos are embedded in audit summaries (not their own rows), so once a user
 * acts on one it's tracked server-side per brand (`dismissed-recommendations`)
 * and filtered out here — it doesn't reappear after navigating away and back.
 */
export function RecommendationList({
  recommendations,
  auditId,
  brandId,
  title = 'Recommandations',
  subtitle,
  dense,
}: {
  recommendations: UnifiedRecommendation[];
  auditId?: string;
  brandId?: string;
  title?: string | null;
  subtitle?: string;
  /** One column instead of two (for narrow side panels). */
  dense?: boolean;
}) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    auditsApi
      .listDismissedRecommendations(brandId)
      .then((ids) => { if (!cancelled) setDismissed(new Set(ids)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [brandId]);

  // When the user actions a reco: hide it locally + persist the dismissal so it
  // stays hidden across navigation.
  const handleActioned = (recoId: string) => {
    setDismissed((prev) => new Set(prev).add(recoId));
    if (brandId) auditsApi.dismissRecommendation(brandId, recoId).catch(() => {});
  };

  const visible = recommendations.filter((r) => !dismissed.has(r.id));
  if (!visible.length) return null;

  return (
    <Box>
      {title !== null && (
        <Typography variant="h6" fontWeight={700} sx={{ mb: subtitle ? 0.5 : 1.5 }}>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}
      <Grid container spacing={1.5}>
        {visible.map((reco) => (
          <Grid key={reco.id} size={{ xs: 12, md: dense ? 12 : 6 }}>
            <RecommendationCard
              reco={reco}
              auditId={auditId}
              brandId={brandId}
              onActioned={() => handleActioned(reco.id)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
